import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Play, Briefcase, Mail, Wand2, 
  X, ChevronRight, Monitor, ArrowRight,
  Star
} from 'lucide-react';
import { playSound, SOUNDS } from '../App';

// Floating particles component
const FloatingParticles = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 3 + 2,
    delay: Math.random() * 2
  }));

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 0
    }}>
      {particles.map(p => (
        <motion.div
          key={p.id}
          initial={{ 
            x: `${p.x}%`, 
            y: `${p.y}%`,
            opacity: 0,
            scale: 0
          }}
          animate={{ 
            y: [`${p.y}%`, `${p.y - 30}%`],
            opacity: [0, 0.6, 0],
            scale: [0, 1, 0]
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeOut"
          }}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: 'rgba(0, 0, 128, 0.4)'
          }}
        />
      ))}
    </div>
  );
};

// Typewriter effect
const TypeWriter = ({ text, speed = 50, delay = 0, onComplete }) => {
  const [displayText, setDisplayText] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const startTimer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(startTimer);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayText(text.substring(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
        onComplete?.();
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed, started, onComplete]);

  return <span>{displayText}<span style={{ opacity: displayText.length < text.length ? 1 : 0 }}>|</span></span>;
};

// Quick action cards data
const QUICK_ACTIONS = [
  {
    id: 'video',
    icon: Play,
    title: 'Se Demo',
    subtitle: 'Opplev Autonomous i aksjon',
    color: '#e74c3c',
    gradient: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
    windowId: 'video',
    desktopIcon: 'video',
    emoji: '🎬'
  },
  {
    id: 'services',
    icon: Briefcase,
    title: 'Våre Tjenester',
    subtitle: 'Hva vi kan bygge for deg',
    color: '#3498db',
    gradient: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
    windowId: 'services',
    desktopIcon: 'services',
    emoji: '💼'
  },
  {
    id: 'appwizard',
    icon: Wand2,
    title: 'Bygg en App',
    subtitle: 'Prøv vår interaktive veiviser',
    color: '#9b59b6',
    gradient: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
    windowId: 'appwizard',
    desktopIcon: 'appwizard',
    emoji: '🧙'
  },
  {
    id: 'contact',
    icon: Mail,
    title: 'Ta Kontakt',
    subtitle: 'Start en samtale med oss',
    color: '#27ae60',
    gradient: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
    windowId: 'contact',
    desktopIcon: 'contact',
    emoji: '📧'
  }
];

export default function WelcomeGuide({ 
  onOpenWindow, 
  onDismiss,
  onHighlightIcon,
  isFirstVisit = true
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [titleComplete, setTitleComplete] = useState(false);

  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      playSound(SOUNDS.windowOpen);
    }, 600);
    
    const contentTimer = setTimeout(() => setShowContent(true), 1000);

    return () => {
      clearTimeout(timer);
      clearTimeout(contentTimer);
    };
  }, []);

  // Highlight icon on hover
  useEffect(() => {
    if (hoveredAction) {
      const action = QUICK_ACTIONS.find(a => a.id === hoveredAction);
      onHighlightIcon?.(action?.desktopIcon || null);
    } else {
      onHighlightIcon?.(null);
    }
  }, [hoveredAction, onHighlightIcon]);

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleActionClick = (action) => {
    playSound(SOUNDS.click);
    setIsVisible(false);
    localStorage.setItem('welcomeGuideShown', 'true');
    
    setTimeout(() => {
      onOpenWindow?.(action.windowId);
      onDismiss?.();
    }, 250);
  };

  const handleExplore = () => {
    playSound(SOUNDS.click);
    setIsVisible(false);
    localStorage.setItem('welcomeGuideShown', 'true');
    setTimeout(() => onDismiss?.(), 250);
  };

  const handleDismiss = () => {
    playSound(SOUNDS.windowClose);
    setIsVisible(false);
    localStorage.setItem('welcomeGuideShown', 'true');
    setTimeout(() => onDismiss?.(), 250);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Animated backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={handleDismiss}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'radial-gradient(ellipse at center, rgba(0,0,40,0.5) 0%, rgba(0,0,0,0.75) 100%)',
              zIndex: 9998,
              backdropFilter: 'blur(2px)'
            }}
          />

          {/* Main Welcome Card */}
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9, rotateX: 10 }}
            animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ 
              duration: 0.5, 
              ease: [0.25, 0.46, 0.45, 0.94],
              type: "spring",
              stiffness: 100,
              damping: 15
            }}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
              width: '90%',
              maxWidth: '520px',
              perspective: '1000px'
            }}
          >
            {/* Windows 95 Window Frame */}
            <div style={{
              background: '#c0c0c0',
              border: '3px solid',
              borderColor: '#ffffff #404040 #404040 #ffffff',
              boxShadow: '10px 10px 0 rgba(0,0,0,0.35), 0 0 80px rgba(0,0,128,0.2), inset 0 0 0 1px rgba(255,255,255,0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Floating particles */}
              <FloatingParticles />

              {/* Title Bar */}
              <div style={{
                background: 'linear-gradient(90deg, #000080 0%, #1084d0 50%, #000080 100%)',
                padding: '6px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <motion.div
                    animate={{ 
                      rotate: [0, 5, -5, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                  >
                    <Sparkles size={14} color="#ffd700" />
                  </motion.div>
                  <span style={{
                    color: 'white',
                    fontFamily: "'Segoe UI', Tahoma, sans-serif",
                    fontSize: '12px',
                    fontWeight: 'bold',
                    textShadow: '1px 1px 0 rgba(0,0,0,0.5)'
                  }}>
                    Velkommen til Autonomous
                  </span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.15, background: '#ff6b6b' }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleDismiss}
                  style={{
                    width: '18px',
                    height: '18px',
                    background: '#c0c0c0',
                    border: '2px solid',
                    borderColor: '#fff #404040 #404040 #fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  <X size={10} />
                </motion.button>
              </div>

              {/* Content */}
              <div style={{ padding: '20px', position: 'relative', zIndex: 1 }}>
                {/* Hero Section */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  {/* Animated Windows Logo */}
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.3, duration: 0.6, type: "spring" }}
                    style={{
                      display: 'inline-grid',
                      gridTemplateColumns: '1fr 1fr',
                      width: '44px',
                      height: '44px',
                      gap: '2px',
                      marginBottom: '14px'
                    }}
                  >
                    {['#ff0000', '#00ff00', '#0000ff', '#ffff00'].map((color, i) => (
                      <motion.div 
                        key={i}
                        animate={{ 
                          boxShadow: [
                            `0 0 0 rgba(${color === '#ff0000' ? '255,0,0' : color === '#00ff00' ? '0,255,0' : color === '#0000ff' ? '0,0,255' : '255,255,0'},0)`,
                            `0 0 10px rgba(${color === '#ff0000' ? '255,0,0' : color === '#00ff00' ? '0,255,0' : color === '#0000ff' ? '0,0,255' : '255,255,0'},0.5)`,
                            `0 0 0 rgba(${color === '#ff0000' ? '255,0,0' : color === '#00ff00' ? '0,255,0' : color === '#0000ff' ? '0,0,255' : '255,255,0'},0)`
                          ]
                        }}
                        transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                        style={{ background: color, borderRadius: '2px' }} 
                      />
                    ))}
                  </motion.div>

                  <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    style={{
                      margin: '0 0 6px 0',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#000080',
                      fontFamily: "'Segoe UI', Tahoma, sans-serif"
                    }}
                  >
                    {showContent ? (
                      <TypeWriter 
                        text="Hva vil du utforske?" 
                        speed={40} 
                        delay={0}
                        onComplete={() => setTitleComplete(true)}
                      />
                    ) : ''}
                  </motion.h1>
                  
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: titleComplete ? 1 : 0 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      margin: 0,
                      fontSize: '12px',
                      color: '#666',
                      fontFamily: "'Segoe UI', Tahoma, sans-serif"
                    }}
                  >
                    Vi bygger AI som fungerer. Velg et sted å starte.
                  </motion.p>
                </div>

                {/* Quick Action Cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px',
                  marginBottom: '14px'
                }}>
                  {QUICK_ACTIONS.map((action, index) => {
                    const Icon = action.icon;
                    const isHovered = hoveredAction === action.id;
                    
                    return (
                      <motion.button
                        key={action.id}
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ 
                          opacity: showContent ? 1 : 0, 
                          y: showContent ? 0 : 20,
                          scale: showContent ? 1 : 0.9
                        }}
                        transition={{ delay: 0.6 + index * 0.08, duration: 0.3 }}
                        onMouseEnter={() => setHoveredAction(action.id)}
                        onMouseLeave={() => setHoveredAction(null)}
                        onClick={() => handleActionClick(action)}
                        whileHover={{ scale: 1.03, y: -3 }}
                        whileTap={{ scale: 0.97 }}
                        style={{
                          padding: '12px',
                          background: isHovered ? action.gradient : 'white',
                          border: '2px solid',
                          borderColor: isHovered 
                            ? `${action.color} ${action.color} ${action.color} ${action.color}`
                            : '#808080 #fff #fff #808080',
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          transition: 'all 0.2s ease',
                          boxShadow: isHovered 
                            ? `0 6px 25px ${action.color}50, inset 0 1px 0 rgba(255,255,255,0.3)`
                            : 'none',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        {/* Shine effect on hover */}
                        {isHovered && (
                          <motion.div
                            initial={{ x: '-100%' }}
                            animate={{ x: '200%' }}
                            transition={{ duration: 0.6 }}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '50%',
                              height: '100%',
                              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                              pointerEvents: 'none'
                            }}
                          />
                        )}
                        
                        <div style={{
                          width: '34px',
                          height: '34px',
                          borderRadius: '6px',
                          background: isHovered ? 'rgba(255,255,255,0.25)' : action.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          fontSize: '16px'
                        }}>
                          {action.emoji}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: isHovered ? 'white' : '#000080',
                            fontFamily: "'Segoe UI', Tahoma, sans-serif"
                          }}>
                            {action.title}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: isHovered ? 'rgba(255,255,255,0.9)' : '#666',
                            fontFamily: "'Segoe UI', Tahoma, sans-serif",
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {action.subtitle}
                          </div>
                        </div>
                        <motion.div
                          animate={{ x: isHovered ? 4 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronRight 
                            size={14} 
                            color={isHovered ? 'white' : '#999'}
                          />
                        </motion.div>
                      </motion.button>
                    );
                  })}
                </div>

                {/* Explore Desktop Button */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 10 }}
                  transition={{ delay: 1 }}
                  onClick={handleExplore}
                  whileHover={{ scale: 1.02, background: '#d8d8d8' }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#c0c0c0',
                    border: '2px solid',
                    borderColor: '#fff #404040 #404040 #fff',
                    cursor: 'pointer',
                    fontFamily: "'Segoe UI', Tahoma, sans-serif",
                    fontSize: '12px',
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Monitor size={14} />
                  Utforsk skrivebordet selv
                  <ArrowRight size={12} />
                </motion.button>

                {/* Keyboard hint */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showContent ? 0.4 : 0 }}
                  transition={{ delay: 1.3 }}
                  style={{
                    margin: '10px 0 0 0',
                    fontSize: '10px',
                    color: '#888',
                    fontFamily: "'Segoe UI', Tahoma, sans-serif",
                    textAlign: 'center'
                  }}
                >
                  Trykk <kbd style={{ 
                    background: '#e0e0e0', 
                    padding: '2px 6px', 
                    borderRadius: '3px',
                    border: '1px solid #999',
                    fontFamily: 'monospace',
                    fontSize: '9px'
                  }}>ESC</kbd> for å lukke
                </motion.p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
